import $ from "jquery"; // tslint:disable-line match-default-export-name
import React from "react";

import { loadJs } from "@use/load-js";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { colors, urls } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { config } from "@apps/viewer/src/ts/components/layout/Header/config";
import { InitialSlide } from "@apps/viewer/src/ts/components/layout/Header/InitialSlide";
import { UserInfo } from "@apps/viewer/src/ts/components/layout/Header/UserInfo";
import { observer } from "mobx-react";

type HeaderProps = {
  /**
   * A reference to the application lifecycle store, so we can update state
   * when needed.
   */
  store: Store;

  /**
   * Specifies if the header should currently be visible (ie. if the
   * application is ready to be rendered).
   */
  visible?: boolean;
};

type HeaderState = {
  /**
   * Specifies if something went wrong during the fetching of the gPanels, since
   * we shouldn't render the status button in that case.
   */
  failedPanelsLoad: boolean;
};

/**
 * The header is rendered at the top of the page, containing basic ui elements
 * such as logo and profile data.
 */
@observer
class HeaderUnwrapped extends React.Component<HeaderProps, HeaderState> {
  public readonly state: HeaderState = {
    failedPanelsLoad: false,
  };

  /**
   * Will contain a reference to the placeholder for the status button, so we
   * can easily inject it on load.
   */
  protected readonly statusRef: React.RefObject<HTMLSpanElement | null> =
    React.createRef();

  /**
   * Fetch the gPanels script and initialize the status-button when the
   * component has been mounted into the DOM.
   */
  public componentDidMount(): void {
    loadJs(urls.gPanels).then(
      () => {
        // Bail out if the component is no longer mounted
        if (!this.statusRef.current || typeof $.fn.gPanels === "undefined") {
          this.setState({ failedPanelsLoad: true });

          return;
        }

        // Bail out if the component is no longer mounted
        if (!this.statusRef.current) {
          return;
        }

        // ... Otherwise render the status button
        // tslint:disable-next-line no-non-null-assertion
        $(this.statusRef.current).gPanels!({
          type: "status",
          suppressUrgentWarnings: process.env.NODE_ENV === "development",
        });
      },

      // Propagate errors while fetching the script
      (err) => {
        throw err;
      },
    );
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    return (
      <InitialSlide in={this.props.visible}>
        {(ref) => (
          <header
            ref={ref}
            className={`${this.props.store.isMobile ? "mobile" : ""}`}
          >
            {this.renderGyldendalLogo()}
            {this.renderCourseTitle()}

            <style jsx>{`
              header {
                /** Put the header on top of everything */
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;

                /** Apply basic sizing to the header */
                box-sizing: border-box;
                height: 60px;
                padding: 12px 30px;

                /** Apply box styling */
                background: ${config.backgroundColor};
                box-shadow: 0 0 50px ${config.dropShadowColor};

                /** Setup flexbox */
                display: flex;
                align-items: center;

                &.mobile {
                  /** Slightly reduce internal padding on mobile */
                  padding: 12px 20px;
                }

                @media (max-width: 768px) {
                  /**
                                 * Position above both progression prompt
                                 * on mobile
                                 */
                  z-index: 1001;
                }
              }
            `}</style>
          </header>
        )}
      </InitialSlide>
    );
  }

  /**
   * Renderer that'll inject the Gyldendal logo on the left site of the course
   * title.
   */
  protected renderGyldendalLogo(): React.JSX.Element {
    return (
      <i className="logo">
        {getIcon("gyldendal", { fillColor: colors.black })}

        <style jsx>{`
          i.logo {
            /** Set up font styling */
            display: inline-block;
            width: 20px;
            height: 30px;

            /** Ensure logo always takes up assigned space */
            flex-shrink: 0;

            /** Apply a bit of margin between logo and title */
            margin-right: 10px;
          }
        `}</style>
      </i>
    );
  }

  /**
   * Renderer that'll inject the title of the course that the user is
   * currently modifying.
   */
  protected renderCourseTitle(): React.JSX.Element {
    return (
      <h1 className={`${this.props.store.isMobile ? "mobile" : ""}`}>
        {this.props.store.course.title}

        <style jsx>{`
          h1 {
            /** Reset default margins */
            margin: 0;
            padding: 0;

            /** Apply font styling */
            color: ${colors.black};
            font-size: 24px;
            line-height: 1em;
            font-weight: normal;

            /** If titles are too long, then truncate text */
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            /** Setup minimum margin between title and tools */
            margin-right: 20px;

            &.mobile {
              font-size: 20px;
            }
          }
        `}</style>
      </h1>
    );
  }

  /**
   * Renderer that'll inject the tool collection at the right side of the
   * topmenu.
   */
  protected renderTools(): React.JSX.Element {
    return (
      <>
        {this.renderStatusButton()}
        {this.renderUserInfo()}
      </>
    );
  }

  /**
   * Renderer that injects the product status button into the header, by
   * creating a placeholder and then using Gyldendals panel plugin to actually
   * inject the button.
   */
  protected renderStatusButton(): React.ReactNode {
    // If the panels aren't available at the moment, then don't bother rendering
    // a button that attempts to interact with them.
    if (this.state.failedPanelsLoad) {
      return;
    }

    return (
      <span
        ref={this.statusRef}
        className={`status ${this.props.store.isMobile ? "mobile" : ""}`}
      >
        <style jsx>{`
          .status {
            /** Set up sizing of the placeholder */
            display: inline-block;
            width: 30px;
            height: 30px;

            /** Pull towards the right side */
            margin-left: auto;

            /**
                         * Apply a bit of margin between this icon and user
                         * info.
                         */
            margin-right: 25px;

            &.mobile {
              /**
                             * Slightly reduce margin between this icon and
                             * user info on mobile
                             */
              margin-right: 20px;
            }
          }
        `}</style>
      </span>
    );
  }

  /**
   * Renderer that injects the <UserInfo /> sub-component into the page, which
   * will let the user sign out of the product.
   */
  protected renderUserInfo(): React.JSX.Element {
    return <UserInfo />;
  }
}

// tslint:disable-next-line variable-name
export const Header = injectStore(HeaderUnwrapped);
